/**
 * V3 Product Visuals — paired GLOBAL (English) / TR (Turkish) scene manager.
 *
 * One source of truth: every row here is the SAME `landing_content_blocks`
 * record the public page reads, grouped into EN/TR pairs by `scene_key` through
 * `/admin/landing-content/product-scenes`. Nothing about order, titles, images
 * or approval is stored twice.
 *
 * A variant is public only when it is APPROVED and its embedded language
 * matches the surface it is scoped to — the server enforces both, this screen
 * only makes the state obvious and editable.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, CheckCircle2, Image as ImageIcon, Loader2, Plus, Upload } from 'lucide-react';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { useToast } from '../../hooks/use-toast';
import { MultilingualInput } from '../../components/MultilingualInput';

const SURFACE_OF = { EN: 'GLOBAL', TR: 'TR' };

const VariantCard = ({ scene, locale, onChanged }) => {
  const variant = scene.variants?.[locale];
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState(null);
  const [alt, setAlt] = useState(null);
  const fileRef = React.useRef(null);
  const approved = variant?.visual_review_status === 'approved';
  const image = variant?.thumbnail_url || variant?.compressed_url || variant?.image_url || '';
  const dirty = JSON.stringify(title) !== JSON.stringify(variant?.title || {})
    || JSON.stringify(alt) !== JSON.stringify(variant?.image_alt || {});

  useEffect(() => {
    setTitle(variant?.title || {});
    setAlt(variant?.image_alt || {});
  }, [variant?.block_id, variant?.updated_at]);   // eslint-disable-line react-hooks/exhaustive-deps

  // Surface the server's own refusal. The approval rules are server-side, so
  // hiding the reason behind a generic line is what turned a precise 422 into
  // "Could not update the GLOBAL variant". Only the safe, structured fields are
  // shown (code + message) — never a stack trace or a storage key — and an
  // authorization failure stays distinguishable from a validation failure.
  const describeError = (err) => {
    const status = err?.response?.status;
    const d = err?.response?.data;
    const body = (d && typeof d === 'object' && d.detail && typeof d.detail === 'object')
      ? d.detail : (d && typeof d === 'object' ? d : {});
    const code = typeof body.code === 'string' ? body.code : '';
    const message = typeof body.message === 'string' ? body.message : '';
    const where = `${SURFACE_OF[locale]} variant`;
    if (status === 401) return `Your session has expired — sign in again to update the ${where}.`;
    if (status === 403) return `You do not have permission to update the ${where}.`;
    if (status === 404) return `The ${where} no longer exists — refresh this panel.`;
    if (status === 429) return `Too many attempts. Please wait a moment and retry the ${where}.`;
    if (message) return code ? `${message} (${code})` : message;
    if (status >= 500) return `The server could not update the ${where} (HTTP ${status}). Please retry.`;
    return `Could not update the ${where}${status ? ` (HTTP ${status})` : ''}`;
  };

  const patch = async (payload, successMessage) => {
    setBusy(true);
    try {
      await api.put(`/admin/landing-content/${variant.block_id}`, payload);
      onChanged(successMessage);
    } catch (err) {
      onChanged(null, describeError(err));
    } finally {
      setBusy(false);
    }
  };

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/admin/landing-content/upload-image', form, {
        headers: { 'Content-Type': undefined },
      });
      if (!data.image_url) throw new Error('upload');
      // Replacing one variant's image never touches its pair, and a replaced
      // image returns the variant to draft so it is reviewed again.
      await api.put(`/admin/landing-content/${variant.block_id}`, {
        image_url: data.image_url,
        thumbnail_url: data.thumbnail_url,
        compressed_url: data.compressed_url,
        image_storage_key: data.image_storage_key,
        image_thumbnail_key: data.image_thumbnail_key,
        image_compressed_key: data.image_compressed_key,
        visual_review_status: 'draft',
      });
      onChanged(`${SURFACE_OF[locale]} image uploaded — it stays in draft until approved`);
    } catch (err) {
      const d = err?.response?.data;
      const detail = (d && typeof d === 'object'
        && (d.detail || d.error) && typeof (d.detail || d.error) === 'string')
        ? (d.detail || d.error) : '';
      onChanged(null, detail
        ? `Upload failed: ${detail}`
        : describeError(err).replace('update', 'upload an image to'));
    } finally {
      setBusy(false);
    }
  };

  if (!variant) {
    return (
      <div className="rounded-lg border border-dashed border-amber-300 bg-amber-50 p-4 text-xs text-amber-800"
           data-testid={`scene-variant-missing-${locale.toLowerCase()}`}>
        The {SURFACE_OF[locale]} ({locale}) variant record is missing for this scene.
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
         data-testid={`scene-variant-${scene.scene_key}-${locale.toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-slate-900">
          {SURFACE_OF[locale]} · {locale === 'EN' ? 'English screen' : 'Turkish screen'}
        </span>
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium ${
          approved ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-600'}`}
              data-testid={`scene-status-${scene.scene_key}-${locale.toLowerCase()}`}>
          {approved ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
          {approved ? 'APPROVED' : 'DRAFT'}
        </span>
      </div>

      <div className="overflow-hidden rounded-md border border-slate-200 bg-slate-50">
        {image ? (
          <img src={image} alt={(alt || {}).en || (title || {}).en || 'Product screen'}
               className="h-36 w-full bg-slate-100 object-contain"
               data-testid={`scene-preview-${scene.scene_key}-${locale.toLowerCase()}`} />
        ) : (
          <div className="grid h-36 place-items-center text-[11px] text-slate-500"
               data-testid={`scene-preview-empty-${scene.scene_key}-${locale.toLowerCase()}`}>
            <span className="flex items-center gap-1"><ImageIcon className="h-3.5 w-3.5" /> No image yet</span>
          </div>
        )}
      </div>

      <div className="space-y-2">
        {/* Caption and alt text are MULTILINGUAL on the record (the canonical
            {en, tr, ...} map the public reader resolves with
            `getLocalizedField`). A single-language input used to PATCH a plain
            string here, which replaced the whole map and destroyed the other
            language. Both fields now use the shared `MultilingualInput` — the
            same editor the Landing and Provider Benefits CMS use — and are
            saved as the full map. Which VARIANT a visitor receives is still
            decided solely by `visual_locale` vs the resolved surface; this is
            only the copy that accompanies it. */}
        <MultilingualInput
          label="Caption"
          value={title}
          onChange={setTitle}
          policy="translatable_optional"
        />
        <div data-testid={`scene-alt-${scene.scene_key}-${locale.toLowerCase()}`}>
          <MultilingualInput
            label="Alt text"
            value={alt}
            onChange={setAlt}
            policy="translatable_optional"
          />
        </div>
        <Button size="sm" variant="outline" disabled={busy || !dirty}
                onClick={() => patch({ title, image_alt: alt }, 'Caption and alt text saved')}
                data-testid={`scene-text-save-${scene.scene_key}-${locale.toLowerCase()}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
          Save caption &amp; alt text
        </Button>
      </div>

      <input type="file" ref={fileRef} accept="image/jpeg,image/png,image/webp"
             className="hidden" onChange={(e) => upload(e.target.files?.[0])}
             data-testid={`scene-file-${scene.scene_key}-${locale.toLowerCase()}`} />
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" disabled={busy}
                onClick={() => fileRef.current?.click()}
                data-testid={`scene-upload-${scene.scene_key}-${locale.toLowerCase()}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
          {image ? 'Replace image' : 'Upload image'}
        </Button>
        <Button size="sm" disabled={busy || !image}
                variant={approved ? 'ghost' : 'default'}
                onClick={() => patch(
                  { visual_review_status: approved ? 'draft' : 'approved' },
                  approved ? `${SURFACE_OF[locale]} approval withdrawn`
                           : `${SURFACE_OF[locale]} variant approved`)}
                data-testid={`scene-approve-${scene.scene_key}-${locale.toLowerCase()}`}>
          {approved ? 'Withdraw approval' : 'Approve'}
        </Button>
      </div>
      {!image && (
        <p className="text-[11px] text-slate-500">
          An image is required before this variant can be approved.
        </p>
      )}
    </div>
  );
};

export const ProductVisualsPanel = ({ sectionKey = null, compact = false }) => {
  const { toast } = useToast();
  const [scenes, setScenes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newKey, setNewKey] = useState('');
  const [creating, setCreating] = useState(false);
  const [previewSurface, setPreviewSurface] = useState('GLOBAL');
  const [effective, setEffective] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/landing-content/product-scenes');
      const all = data.scenes || [];
      // When embedded inside a section editor, only that section's scenes are
      // shown — the images sit with the text they belong to.
      setScenes(sectionKey ? all.filter((s) => s.section_key === sectionKey) : all);
    } catch {
      toast({ title: 'Could not load product visuals', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, sectionKey]);

  useEffect(() => { load(); }, [load]);

  const onChanged = (message, error) => {
    if (error) toast({ title: error, variant: 'destructive' });
    else if (message) toast({ title: message });
    load();
  };

  const createScene = async () => {
    if (!newKey.trim()) return;
    setCreating(true);
    try {
      await api.post('/admin/landing-content/product-scenes', {
        scene_key: newKey.trim(),
        section_key: sectionKey || 'showcase',
        display_order: (scenes.length + 1) * 10,
      });
      setNewKey('');
      onChanged('Scene created with an English and a Turkish slot');
    } catch {
      onChanged(null, 'Could not create the scene');
    } finally {
      setCreating(false);
    }
  };

  // "What would this surface actually serve?" — a read-only check against the
  // public endpoint. It never changes the published homepage.
  const checkEffective = async (surface) => {
    setPreviewSurface(surface);
    try {
      const { data } = await api.get(
        `/admin/landing-content/effective-preview?surface=${surface}`);
      setEffective(data);
    } catch {
      setEffective({ error: true });
    }
  };

  const setOrder = async (scene, order) => {
    const ids = ['EN', 'TR'].map((l) => scene.variants?.[l]?.block_id).filter(Boolean);
    await Promise.all(ids.map((id) => api.put(`/admin/landing-content/${id}`,
      { display_order: order })));
    onChanged('Scene order updated for both variants');
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-slate-500"
           data-testid="product-visuals-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading product visuals…
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="product-visuals-panel">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <p className="text-sm font-semibold text-slate-900">
          {compact ? 'Images for this section' : 'V3 product visuals'}
        </p>
        <p className="mt-1 text-xs text-slate-600">
          Each scene is the same TreatX screen in two languages: the English image is
          served on the GLOBAL surface, the Turkish image on the Türkiye surface.
          Nothing is published until you approve it, and the surface is always decided
          by the server — never by the visitor.
        </p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-[11px]">New scene key</Label>
            <Input value={newKey} onChange={(e) => setNewKey(e.target.value)}
                   placeholder="e.g. treatment_discovery"
                   className="h-8 w-56 text-sm"
                   data-testid="product-visuals-new-key" />
          </div>
          <Button size="sm" onClick={createScene} disabled={creating || !newKey.trim()}
                  data-testid="product-visuals-create">
            {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
            Add scene
          </Button>
          {!compact && (
            <a href="#/treatx-admin/v3-draft-preview"
               className="inline-flex h-8 items-center gap-1.5 rounded-md border border-slate-200 px-3 text-xs font-medium text-slate-700 hover:bg-slate-50"
               data-testid="product-visuals-draft-review-link">
              Review the whole V3 candidate
            </a>
          )}
          <div className="ml-auto flex items-center gap-2">
            {['GLOBAL', 'TR'].map((s) => (
              <Button key={s} size="sm"
                      variant={previewSurface === s && effective ? 'default' : 'outline'}
                      onClick={() => checkEffective(s)}
                      data-testid={`product-visuals-effective-${s.toLowerCase()}`}>
                Check {s} result
              </Button>
            ))}
          </div>
        </div>
        {effective && (
          <div className="mt-3 rounded-lg bg-slate-50 p-3 text-xs text-slate-700"
               data-testid="product-visuals-effective-result">
            {effective.error ? 'Could not read the effective result.' : (
              <>
                <span className="font-semibold">{effective.surface}</span> would serve{' '}
                {(effective.scenes || []).length} approved scene
                {(effective.scenes || []).length === 1 ? '' : 's'}:{' '}
                {(effective.scenes || []).map((s) => s.scene_key).join(', ') || '—'}
              </>
            )}
          </div>
        )}
      </div>

      {scenes.length === 0 && (
        <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500"
           data-testid="product-visuals-empty">
          No product scenes yet. Add one above, then upload its English and Turkish images.
        </p>
      )}

      {scenes.map((scene) => (
        <div key={scene.scene_key} className="rounded-xl border border-slate-200 bg-slate-50 p-4"
             data-testid={`product-scene-${scene.scene_key}`}>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">{scene.scene_key}</p>
              <p className="text-[11px] text-slate-500">section: {scene.section_key}</p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                scene.complete ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}
                    data-testid={`product-scene-readiness-${scene.scene_key}`}>
                {scene.complete ? 'BOTH VARIANTS READY' : 'INCOMPLETE'}
              </span>
              <label className="flex items-center gap-1 text-[11px] text-slate-600">
                Order
                <Input type="number" defaultValue={scene.display_order}
                       onBlur={(e) => Number(e.target.value) !== scene.display_order
                         && setOrder(scene, Number(e.target.value))}
                       className="h-7 w-20 text-xs"
                       data-testid={`product-scene-order-${scene.scene_key}`} />
              </label>
            </div>
          </div>
          <div className="mt-4 grid gap-4 lg:grid-cols-2">
            <VariantCard scene={scene} locale="EN" onChanged={onChanged} />
            <VariantCard scene={scene} locale="TR" onChanged={onChanged} />
          </div>
        </div>
      ))}
    </div>
  );
};

export default ProductVisualsPanel;
